import React, { useEffect, useState } from 'react';
import type { Goal } from '../types';

interface ShotPositionAnalyticsProps {
  goals: Goal[];
  width?: number;
  height?: number;
}

export default function ShotPositionAnalytics({ goals, width = 400, height = 300 }: ShotPositionAnalyticsProps) {
  const [diagramSize, setDiagramSize] = useState({ width, height });

  // Adjust diagram size for mobile
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 768) {
        const responsiveWidth = Math.min(window.innerWidth - 40, width);
        const aspectRatio = height / width;
        const responsiveHeight = responsiveWidth * aspectRatio;
        setDiagramSize({ 
          width: responsiveWidth, 
          height: responsiveHeight 
        });
      } else {
        setDiagramSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [width, height]);

  // Calculate zones (4x4 grid)
  const getZone = (x: number | null, y: number | null): number => {
    if (x === null || y === null) return -1;
    const col = Math.floor(x / 25);
    const row = Math.floor(y / 25);
    return row * 4 + col;
  };

  // Count goals in each zone
  const zoneCount = new Array(16).fill(0);
  goals.forEach(goal => {
    const zone = getZone(goal.shotXPosition, goal.shotYPosition);
    if (zone >= 0 && zone < 16) {
      zoneCount[zone]++;
    }
  });

  // Calculate percentages
  const goalsWithShotPosition = goals.filter(goal => goal.shotXPosition !== null && goal.shotYPosition !== null);
  const totalGoals = goalsWithShotPosition.length;
  const zonePercentages = zoneCount.map(count => 
    totalGoals > 0 ? (count / totalGoals) * 100 : 0
  );

  return (
    <div className="flex justify-center items-center w-full">
      <svg
        width={diagramSize.width}
        height={diagramSize.height}
        viewBox="0 0 100 100"
        className="border-4 border-[#1b3968] rounded-lg"
      >
        {/* Background Image */}
        <image
          href="https://cdn.shopify.com/s/files/1/0609/6397/9463/files/Half_hockey_rink.png?v=1743095801"
          x="0"
          y="0"
          width="100"
          height="100"
          preserveAspectRatio="none"
        />
        
        {/* Zone grid */}
        <g stroke="#d1d5db" strokeWidth="0.5">
          {[25, 50, 75].map((pos) => (
            <React.Fragment key={pos}>
              <line x1={pos} y1="0" x2={pos} y2="100" />
              <line x1="0" y1={pos} x2="100" y2={pos} />
            </React.Fragment>
          ))}
        </g>

        {/* Zone percentages */}
        {zonePercentages.map((percentage, index) => {
          const col = index % 4;
          const row = Math.floor(index / 4);
          const x = col * 25 + 12.5;
          const y = row * 25 + 12.5;

          return percentage > 0 ? (
            <g key={index}>
              <rect
                x={col * 25}
                y={row * 25}
                width="25"
                height="25"
                fill={`rgba(239, 68, 68, ${percentage / 100})`}
                className="transition-all duration-300"
              />
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="font-bold"
                style={{ fontSize: '8px', fill: percentage > 50 ? '#ffffff' : '#000000' }}
              >
                {percentage.toFixed(0)}%
              </text>
            </g>
          ) : null;
        })}
      </svg>
    </div>
  );
}